import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, LogIn, LogOut, LayoutDashboard, User } from "lucide-react";
import { NAV, SITE } from "../../lib/content";
import { useAuth } from "../../context/AuthContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, logout, promptLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <>
      {/* Admissions banner */}
      <div
        data-testid="admissions-banner"
        className="relative z-[60] bg-flame text-white overflow-hidden"
      >
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-center gap-3 text-center">
          <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
          <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em]">
            Admissions Open · Free Demo Class Available · Join Today
          </p>
        </div>
      </div>

      <header
        data-testid="navbar"
        className={`sticky top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 border-b ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-ink/10 shadow-[0_1px_30px_rgba(10,17,40,0.06)]"
            : "bg-white/40 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => go("home")}
            data-testid="logo-button"
            className="flex items-center gap-2 group"
          >
            <span className="grid place-items-center h-9 w-9 bg-ink text-white font-heading font-extrabold text-sm rounded-md group-hover:bg-flame transition-colors duration-300">
              NK
            </span>
            <span className="font-heading font-extrabold tracking-tight text-ink text-[15px] leading-none hidden sm:block">
              Science &amp; Physics<span className="text-flame">.</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => go(n.id)}
                className="px-3 py-2 text-sm font-medium text-ink/70 hover:text-ink relative group transition-colors"
              >
                {n.label}
                <span className="absolute left-3 right-3 -bottom-0.5 h-0.5 bg-flame scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="hidden sm:flex items-center gap-2">
                {user.role === "admin" && (
                  <button
                    onClick={() => navigate("/admin")}
                    data-testid="nav-admin-link"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-ink/70 hover:text-flame px-3 py-2 transition-colors"
                  >
                    <LayoutDashboard className="h-4 w-4" /> Admin
                  </button>
                )}
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink/70">
                  <User className="h-4 w-4 text-flame" /> {user.name || "Student"}
                </span>
                <button
                  onClick={logout}
                  data-testid="nav-logout"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-flame px-2 py-2 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => promptLogin()}
                data-testid="nav-login-button"
                className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-ink/70 hover:text-flame px-3 py-2 transition-colors"
              >
                <LogIn className="h-4 w-4" /> Login
              </button>
            )}
            <a
              href={`tel:${SITE.phoneIntl}`}
              data-testid="nav-call-button"
              className="hidden sm:inline-flex items-center gap-2 bg-ink text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-flame transition-colors duration-300"
            >
              <Phone className="h-4 w-4" /> Call Now
            </a>
            <button
              className="lg:hidden text-ink"
              data-testid="menu-toggle"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav
              data-testid="mobile-menu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden bg-white border-t border-ink/10"
            >
              <div className="px-6 py-4 flex flex-col">
                {NAV.map((n) => (
                  <button
                    key={n.id}
                    data-testid={`mobile-nav-${n.id}`}
                    onClick={() => go(n.id)}
                    className="py-3 text-left text-ink/80 font-medium border-b border-ink/5 last:border-0"
                  >
                    {n.label}
                  </button>
                ))}
                <a
                  href={`tel:${SITE.phoneIntl}`}
                  className="mt-4 inline-flex items-center justify-center gap-2 bg-flame text-white font-semibold px-5 py-3 rounded-full"
                >
                  <Phone className="h-4 w-4" /> Call {SITE.phone}
                </a>
                {user ? (
                  <div className="mt-3 flex flex-col gap-2">
                    {user.role === "admin" && (
                      <button
                        onClick={() => { setOpen(false); navigate("/admin"); }}
                        data-testid="mobile-admin-link"
                        className="inline-flex items-center justify-center gap-2 border border-ink/15 text-ink font-semibold px-5 py-3 rounded-full"
                      >
                        <LayoutDashboard className="h-4 w-4" /> Admin Dashboard
                      </button>
                    )}
                    <button
                      onClick={() => { setOpen(false); logout(); }}
                      data-testid="mobile-logout"
                      className="inline-flex items-center justify-center gap-2 text-ink/70 font-semibold px-5 py-3"
                    >
                      <LogOut className="h-4 w-4" /> Logout ({user.name || "Student"})
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setOpen(false); promptLogin(); }}
                    data-testid="mobile-login-button"
                    className="mt-3 inline-flex items-center justify-center gap-2 border border-ink/15 text-ink font-semibold px-5 py-3 rounded-full"
                  >
                    <LogIn className="h-4 w-4" /> Student Login
                  </button>
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}

export { scrollTo };
